/* eslint-disable no-console */
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";
import { EMPTY_GUID } from "@iventis/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSitemapApiContext } from "../context/sitemap-api-context";
import { useSitemapRoutingContext } from "../context/sitemap-routing-context";
import { createEmptySitemapVersion, hasSitemapVersionChanged, isSitemapVersionValid } from "../sitemap-helpers";

const SITEMAP_VERSION_KEY = "sitemap-version";

/** Handles sitemap version data fetching and modification */
export function useSitemapVersion() {
    const queryClient = useQueryClient();
    const { getSitemapVersion, updateSitemapVersion, createSitemapVersion, deleteSitemapVersionLevel: deleteSitemapVersionLevelApiRequest } = useSitemapApiContext();
    const { getSelectedSitemapId, getSelectedSitemapVersionId, setSelectedSitemapVersionId } = useSitemapRoutingContext();

    const sitemapId = getSelectedSitemapId();
    const sitemapVersionId = getSelectedSitemapVersionId();

    // If sitemap version id is an empty uuid then we know it is newly created
    const isNewSitemapVersion = sitemapVersionId === EMPTY_GUID;

    // If it is a new sitemap version set sitemap version as an empty one
    const [sitemapVersion, setSitemapVersion] = useState<SitemapVersion | null>(
        isNewSitemapVersion ? createEmptySitemapVersion(sitemapId) : queryClient.getQueryData<SitemapVersion>([SITEMAP_VERSION_KEY, sitemapVersionId]) ?? null
    );

    const { isLoading, data: savedSitemapVersion, refetch } = useQuery(
        [SITEMAP_VERSION_KEY, sitemapVersionId],
        async () => {
            const response = await getSitemapVersion(sitemapId, sitemapVersionId);
            setSitemapVersion(response);
            return response;
        },
        {
            enabled: sitemapVersionId != null && !isNewSitemapVersion,
            retry(failureCount, error) {
                if (failureCount < 3 && error) {
                    return true;
                }
                return false;
            },
            retryDelay: 5000,
        }
    );

    const updateMutation = useMutation(updateSitemapVersion, {
        onMutate: () => {
            queryClient.setQueryData([SITEMAP_VERSION_KEY, sitemapVersionId], sitemapVersion);
        },
    });

    const createMutation = useMutation(createSitemapVersion, {
        onSuccess: (sitemapVersion) => {
            queryClient.setQueryData([SITEMAP_VERSION_KEY, sitemapVersionId], sitemapVersion);
            setSelectedSitemapVersionId(sitemapVersion.id);
        },
    });

    const canSitemapVersionSave = isSitemapVersionValid(sitemapVersion) && hasSitemapVersionChanged(savedSitemapVersion, sitemapVersion);

    const deleteSitemapVersionLevel = async (sitemapVersionLevelId: string) => {
        const updatedSitemapVersion = {
            ...sitemapVersion,
            sitemapVersionLevels: sitemapVersion.sitemapVersionLevels.filter((svl) => svl.id !== sitemapVersionLevelId),
        };
        setSitemapVersion(updatedSitemapVersion);
        try {
            await deleteSitemapVersionLevelApiRequest(sitemapVersionLevelId, sitemapVersionId, sitemapId);
        } catch (err) {
            console.error(err);
        }
    };

    const saveSitemapVersion = () => {
        if (isNewSitemapVersion) {
            createMutation.mutateAsync(sitemapVersion);
        } else {
            updateMutation.mutateAsync(sitemapVersion);
        }
    };

    return {
        sitemapVersion,
        isSitemapVersionLoading: isLoading && !isNewSitemapVersion,
        setSitemapVersion,
        saveSitemapVersion,
        deleteSitemapVersionLevel,
        canSitemapVersionSave,
        isSaving: createMutation.isLoading || updateMutation.isLoading,
        refetchSitemapVersion: refetch,
    };
}
